import { useState, useRef, useCallback, useEffect } from 'react';

const useWebSocket = (url, callbacks = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const callbacksRef = useRef(callbacks);

  // Keep callbacksRef current on every render so ws handlers always call the latest version
  useEffect(() => {
    callbacksRef.current = callbacks;
  });

  const connect = useCallback((initialMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const ws = new WebSocket(url);

      ws.onopen = () => {
        setIsConnected(true);
        if (callbacksRef.current.onConnect) callbacksRef.current.onConnect({ url });

        try {
          ws.send(initialMessage || '#clientStart#');
        } catch (error) {
          if (callbacksRef.current.onError) callbacksRef.current.onError(`Handshake send failed: ${error.message || 'unknown error'}`);
        }
      };

      ws.onmessage = (event) => {
        if (callbacksRef.current.onMessage) callbacksRef.current.onMessage(event.data);
      };

      ws.onerror = () => {
        if (callbacksRef.current.onError) callbacksRef.current.onError('WebSocket transport error occurred');
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        if (callbacksRef.current.onDisconnect) {
          callbacksRef.current.onDisconnect({
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean
          });
        }
        wsRef.current = null;
      };

      wsRef.current = ws;
    } catch (error) {
      if (callbacksRef.current.onError) callbacksRef.current.onError(error.message);
    }
  }, [url]);

  const disconnect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.close(1000, 'Client disconnecting');
    }
  }, []);

  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(message);
      return true;
    }
    return false;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  return {
    isConnected,
    connect,
    disconnect,
    sendMessage
  };
};

export default useWebSocket;
